import {
  lazy,
  memo,
  Suspense,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GetPitchByIdResponse, PitchFormData } from "../Models/PitchFormData";
import Select from "react-select";
import YesNoDialog from "../common/YesNoDialog";
import { PitchTeamData } from "../Models/PitchTeamData";
import { DialogType } from "../Models/DialogType";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSavePitch } from "../../api/mutations/Pitch/useSavePitch";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { useAppContext } from "../../Context/AppContext";
import { useDeletePitch } from "../../api/mutations/Pitch/useDeletePitch";
import { Button } from "react-bootstrap";
import { BsFloppy } from "react-icons/bs";
import { countries } from "countries-list";
import { useGetPitchesByUser } from "../../api/queries/Pitch/useGetPitchesByUser";
import { SORTED_SECTORS } from "../../utils/sectors";
import { useGetCompanyDetails } from "../../api/queries/MuslimInvest/useGetCompanyDetails";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const AllPitches = lazy(() => import("./AllPitches"));
const UploadFiles = lazy(() => import("./UploadFiles"));

const DEFAULT_FORM_DATA: PitchFormData = {
  id: 0,
  status: "Draft",
  name: "",
  companyName: "",
  companyRegistrationNumber: "",
  countryOfRegistration: "",
  companyDescription: "",
  mission: "",
  vision: "",
  sector: "",
  investmentType: "",
  investmentTicketSize: "",
  region: "",
  identifiedMarketProblem: "",
  solution: "",
  marketSize: "",
  growthPotential: "",
  targetAudience: "",
  revenueStreams: "",
  pricingStrategy: "",
  revenueForecast: "",
  profitMargins: "",
  breakEvenMargins: "",
  amountSought: "" as unknown as number,
  useOfFunds: "",
  teamMembers: [] as PitchTeamData[],
  files: [],
  pitchFiles: [],
};

const COUNTRY_OPTIONS = Object.entries(countries)
  .map(([_, country]) => ({ value: country.name, label: country.name }))
  .sort((a, b) => a.label.localeCompare(b.label));

const SECTOR_OPTIONS = SORTED_SECTORS.map((s) => ({ value: s, label: s }));

const INVESTMENT_SIZES = [
  { value: "£1k-10k", label: "£1k-£10k" },
  { value: "£10k-£50k", label: "£10k-£50k" },
  { value: "£50k-£100k", label: "£50k-£100k" },
  { value: "£100k-£500k", label: "£100k-£500k" },
  { value: "£500k-£1M", label: "£500k-£1M" },
  { value: "£1M-£5M", label: "£1M-£5M" },
  { value: "£5M+", label: "£5M+" },
];

const VALIDATION_MESSAGE_STYLE: React.CSSProperties = { fontSize: "0.875rem" };

function normalizeSectors(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as string[];
  if (typeof value === "string") {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function CreatePitch() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("create");
  const { mutateAsync: savePitchMutation, isPending } = useSavePitch();

  const [formData, setFormData] = useState<PitchFormData>(DEFAULT_FORM_DATA);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number>(-1);
  const [dialogMessage, setDialogMessage] = useState<string>("");
  const [dialogType, setDialogType] = useState<string>("");
  const [pitchStep, setPitchStep] = useState<number>(0);
  const { user: userData, objectId } = useAppContext();

  const queryClient = useQueryClient();

  const { data: pitchesData, refetch: fetchPitches } = useGetPitchesByUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const { data: companyData } = useGetCompanyDetails({
    userId: userData?.user.id.toString() || "",
  });

  const companyProfile = companyData ? companyData[0] : null;
  const pitch = useMemo(
    () => pitchesData?.pitches[0]?.pitch ?? null,
    [pitchesData],
  );

  useEffect(() => {
    if (userData?.user.isAdmin === 1) {
      localStorage.setItem("isAdmin", "true");
    }
  }, [userData?.user?.isAdmin]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors: pitchErrors },
  } = useForm<PitchFormData>({
    defaultValues: pitch ?? { status: "Draft" },
  });

  useEffect(() => {
    if (pitch) {
      reset(pitch);
    } else {
      reset({ status: "Draft" });
    }
  }, [pitch, reset]);

  const { mutate: deletePitch } = useDeletePitch({
    onSuccess: () => {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ["user-pitches"] });
      fetchPitches();
      toast.success(t("pitch.deletedSuccess"));
      reset({ status: "Draft" });
    },
  });

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  const submitPitch = async (data: PitchFormData) => {
    const formDataToSend = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      // ✅ Only send id if greater than 0
      if (key === "id" && typeof value === "number" && value <= 0) {
        return; // skip adding id
      }

      if (key === "files" && Array.isArray(value)) {
        value.forEach((file) => {
          if (file instanceof File) {
            formDataToSend.append("files", file);
          }
        });
      } else if (key === "teamMembers" && Array.isArray(value)) {
        formDataToSend.append("teamMembers", JSON.stringify(value));
      } else if (typeof value === "number" || typeof value === "boolean") {
        formDataToSend.append(key, value.toString());
      } else if (typeof value === "string") {
        formDataToSend.append(key, value);
      } else if (value !== undefined && value !== null) {
        formDataToSend.append(key, JSON.stringify(value));
      } else {
        formDataToSend.append(key, "");
      }
    });

    if (objectId !== null) {
      formDataToSend.append("CreatedByObjectId", objectId);
      formDataToSend.append("UserId", userData?.user.id.toString() || "");
    }

    if (companyProfile?.id !== null) {
      formDataToSend.append("CompanyId", companyProfile?.id.toString() || "");
    }
    const responsePitch = await savePitchMutation(formDataToSend);
    // ✅ Optional: fetch team members after pitch creation/update

    const responseTeamMembers = await axiosInstance.get(
      getAPIURL(`/Pitch/team-members-by-pitch-id/${responsePitch.data.id}`),
    );
    const teamMembers = responseTeamMembers.data;

    // ✅ Update local state
    setFormData((prevData) => ({
      ...prevData,
      ...responsePitch.data,
      ...teamMembers,
    }));
    setPitchStep(1);
  };

  const handleEditPitch = useCallback(
    async (id: number) => {
      try {
        setLoading(true);

        // await getPitchFiles(id);

        const responsePitch = await axiosInstance.get<GetPitchByIdResponse>(
          getAPIURL(`/Pitch/get-pitch-by-id/${id}?id=${id}`),
        );

        // 2️⃣ Get team members
        const responseTeamMembers = await axiosInstance.get(
          getAPIURL(`/Pitch/team-members-by-pitch-id/${id}`),
        );

        const teamMembers = responseTeamMembers.data;

        setFormData((prevData) => ({
          ...prevData,
          ...responsePitch.data.pitchViewModel.pitch,
          ...teamMembers,
        }));
        setPitchStep(0);
        reset(responsePitch.data.pitchViewModel.pitch ?? { status: "Draft" });
        setActiveTab("create");
      } catch (error) {
        toast.error(t("pitch.errorFetchingPitch"));
      } finally {
        setLoading(false);
      }
    },
    [reset, t],
  );

  const handleDeletePitch = useCallback(
    (pitch: PitchFormData) => {
      setIdToDelete(pitch.id);
      setDialogType(DialogType.DeletePitch.toString());
      setDialogMessage(t("pitch.deleteConfirm", { name: pitch.name }));
      setDialogVisible(true);
    },
    [t],
  );

  const handleDialogClose = useCallback(
    (result: boolean) => {
      if (result) {
        switch (dialogType) {
          case "DeletePitch":
            deletePitch(idToDelete);
            break;
          default:
            break;
        }
      }
      setDialogVisible(false);
    },
    [dialogType, idToDelete, deletePitch],
  );

  const disableInput = useCallback(() => {
    return formData.status === "Submitted" || formData.status === "Accepted";
  }, [formData.status]);

  const pitchesList = useMemo(() => pitchesData?.pitches || [], [pitchesData]);

  const handleTabCreate = useCallback(() => {
    handleTabChange("create");
    setFormData(DEFAULT_FORM_DATA);
  }, [handleTabChange]);

  const handleTabView = useCallback(() => {
    fetchPitches();
    startTransition(() => {
      handleTabChange("view");
      setPitchStep(0);
    });
  }, [handleTabChange, fetchPitches]);

  return (
    <div className="">
      <YesNoDialog
        title={t("common.confirmation")}
        message={dialogMessage}
        show={dialogVisible}
        onClose={handleDialogClose}
      />
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="container-fluid px-0 pb-4">
        <div className="nav nav-tabs mb-3">
          <button
            className={`nav-link ${
              activeTab === "create" ? "active text-primary" : "text-dark"
            }`}
            onClick={handleTabCreate}
          >
            {t("pitch.createNewPitch")}
          </button>
          <button
            className={`nav-link ${
              activeTab === "view" ? "active text-primary" : "text-dark"
            }`}
            onClick={handleTabView}
          >
            {t("pitch.viewMyPitches")}
          </button>
        </div>
        {activeTab === "create" ? (
          <>
            {disableInput() && (
              <>
                <div className="alert alert-primary" role="alert">
                  {t("pitch.readOnlyAlert", {
                    status: formData.status.toLowerCase(),
                  })}
                </div>
              </>
            )}

            {pitchStep === 0 ? (
              <form onSubmit={handleSubmit(submitPitch)}>
                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.businessOverview")}</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">{t("pitch.pitchName")}</label>
                      <input
                        type="text"
                        {...register("name", {
                          required: t("pitch.validation.nameRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.name ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterPitchName")}
                      />
                      {pitchErrors.name && (
                        <div className="invalid-feedback">
                          {pitchErrors.name.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        {t("admin.events.description")}
                      </label>
                      <textarea
                        {...register("companyDescription", {
                          required: t("pitch.validation.descriptionRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.companyDescription ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterDescription")}
                      />
                      {pitchErrors.companyDescription && (
                        <div className="invalid-feedback">
                          {pitchErrors.companyDescription.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">{t("pitch.mission")}</label>
                      <input
                        type="text"
                        {...register("mission", {
                          required: t("pitch.validation.missionRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.mission ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterMission")}
                      />
                      {pitchErrors.mission && (
                        <div className="invalid-feedback">
                          {pitchErrors.mission.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">{t("pitch.vision")}</label>
                      <input
                        type="text"
                        {...register("vision", {
                          required: t("pitch.validation.visionRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.vision ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterVision")}
                      />
                      {pitchErrors.vision && (
                        <div className="invalid-feedback">
                          {pitchErrors.vision.message}
                        </div>
                      )}
                    </div>

                    <div className="mt-3 mb-3">
                      <label className="mb-3">
                        {t("events.sectorsOfInterest")}
                      </label>
                      <Controller
                        name="sector"
                        control={control}
                        rules={{
                          required: t("events.atLeastOneSector"),
                        }}
                        render={({ field }) => {
                          const arr = normalizeSectors(field.value);
                          return (
                            <div>
                              <Select
                                options={SECTOR_OPTIONS}
                                isMulti
                                isDisabled={disableInput()}
                                placeholder={t(
                                  "pitch.placeholder.selectSectors",
                                )}
                                value={arr.map((val) => ({
                                  value: val,
                                  label: val,
                                }))}
                                onChange={(selected) => {
                                  const values = (
                                    selected as {
                                      value: string;
                                      label: string;
                                    }[]
                                  ).map((opt) => opt.value);
                                  field.onChange(values);
                                }}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor: pitchErrors.sector
                                      ? "#dc3545"
                                      : base.borderColor,
                                  }),
                                }}
                                classNamePrefix="react-select"
                              />
                              {/* Validation Message */}
                              {pitchErrors.sector && (
                                <div
                                  className="text-danger mt-1"
                                  style={VALIDATION_MESSAGE_STYLE}
                                >
                                  {pitchErrors.sector.message}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.investmentType")}
                      </label>
                      <input
                        type="text"
                        {...register("investmentType", {
                          required: t(
                            "pitch.validation.investmentTypeRequired",
                          ),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.vision ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterInvestmentType")}
                      />
                      {pitchErrors.investmentType && (
                        <div className="invalid-feedback">
                          {pitchErrors.investmentType.message}
                        </div>
                      )}
                    </div>

                    <div className="mt-3 mb-3">
                      <label className="mb-3">
                        {t("pitch.investmentTicketSize")}
                      </label>
                      <Controller
                        name="investmentTicketSize"
                        control={control}
                        rules={{
                          required: t(
                            "pitch.validation.investmentTicketSizeRequired",
                          ),
                        }}
                        render={({ field }) => {
                          // Convert selected values to {value, label} format for React Select
                          const value = INVESTMENT_SIZES.filter((opt) =>
                            field.value?.includes(opt.value),
                          ).map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                          }));

                          return (
                            <div>
                              <Select
                                options={INVESTMENT_SIZES}
                                isMulti
                                isDisabled={disableInput()}
                                placeholder={t(
                                  "pitch.placeholder.selectInvestmentTicketSizes",
                                )}
                                value={value}
                                onChange={(selected) => {
                                  const values = (
                                    selected as {
                                      value: string;
                                      label: string;
                                    }[]
                                  ).map((opt) => opt.value);
                                  field.onChange(values);
                                }}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor:
                                      pitchErrors.investmentTicketSize
                                        ? "#dc3545"
                                        : base.borderColor,
                                  }),
                                }}
                                classNamePrefix="react-select"
                              />
                              {pitchErrors.investmentTicketSize && (
                                <div
                                  className="text-danger mt-1"
                                  style={VALIDATION_MESSAGE_STYLE}
                                >
                                  {pitchErrors.investmentTicketSize.message}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                    </div>

                    <div className="mt-3 mb-3">
                      <label className="mb-3">{t("pitch.region")}</label>
                      <select
                        {...register("region", {
                          required: t("pitch.validation.regionRequired"),
                        })}
                        className={`form-select ${
                          pitchErrors.region ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                      >
                        <option value="">{t("pitch.pleaseSelect")}</option>

                        {COUNTRY_OPTIONS.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      {pitchErrors.region && (
                        <div className="invalid-feedback">
                          {pitchErrors.region.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.problemStatement")}</h4>
                  <div className="card-body">
                    <label className="mb-3">
                      {t("pitch.marketProblemQuestion")}
                    </label>
                    <div className="mb-3">
                      <textarea
                        {...register("identifiedMarketProblem", {
                          required: t("pitch.validation.descriptionRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.identifiedMarketProblem
                            ? "is-invalid"
                            : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t(
                          "pitch.placeholder.enterIdentifiedMarketProblem",
                        )}
                      />
                      {pitchErrors.identifiedMarketProblem && (
                        <div className="invalid-feedback">
                          {pitchErrors.identifiedMarketProblem.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.proposedSolutionDetails")}</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">{t("pitch.solution")}</label>
                      <textarea
                        {...register("solution", {
                          required: t("pitch.validation.solutionRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.solution ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterSolution")}
                      />
                      {pitchErrors.solution && (
                        <div className="invalid-feedback">
                          {pitchErrors.solution.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.marketOpportunity")}</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.marketSizeQuestion")}
                      </label>
                      <select
                        {...register("marketSize", {
                          required: t("pitch.validation.marketSizeRequired"),
                        })}
                        className={`form-select ${
                          pitchErrors.marketSize ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                      >
                        <option value="">{t("pitch.pleaseSelect")}</option>
                        <option value="<1m">&lt;1m</option>
                        <option value="1m-5m">1m-5m</option>
                        <option value="5m-10m">5m-10m</option>
                        <option value="10m-50m">10m-50m</option>
                        <option value="50m-100m">50m-100m</option>
                        <option value="500m-1b">500m-1b</option>
                        <option value="1b+">1b+</option>
                      </select>
                      {pitchErrors.marketSize && (
                        <div className="invalid-feedback">
                          {pitchErrors.marketSize.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.growthPotentialQuestion")}
                      </label>
                      <textarea
                        {...register("growthPotential", {
                          required: t(
                            "pitch.validation.growthPotentialRequired",
                          ),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.growthPotential ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t(
                          "pitch.placeholder.enterGrowthPotential",
                        )}
                      />
                      {pitchErrors.growthPotential && (
                        <div className="invalid-feedback">
                          {pitchErrors.growthPotential.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.targetAudienceQuestion")}
                      </label>
                      <textarea
                        {...register("targetAudience", {
                          required: t(
                            "pitch.validation.targetAudienceRequired",
                          ),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.targetAudience ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterTargetAudience")}
                      />
                      {pitchErrors.targetAudience && (
                        <div className="invalid-feedback">
                          {pitchErrors.targetAudience.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.businessModel")}</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.revenueStreamsQuestion")}
                      </label>
                      <textarea
                        {...register("revenueStreams", {
                          required: t("pitch.validation.revenueStreamRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.revenueStreams ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterRevenueStream")}
                      />
                      {pitchErrors.revenueStreams && (
                        <div className="invalid-feedback">
                          {pitchErrors.revenueStreams.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.pricingStrategyQuestion")}
                      </label>

                      <textarea
                        {...register("pricingStrategy", {
                          required: t(
                            "pitch.validation.pricingStrategyRequired",
                          ),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.pricingStrategy ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t(
                          "pitch.placeholder.enterPricingStrategy",
                        )}
                      />
                      {pitchErrors.pricingStrategy && (
                        <div className="invalid-feedback">
                          {pitchErrors.pricingStrategy.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.financialProjections")}</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.revenueForecastQuestion")}
                      </label>

                      <textarea
                        {...register("revenueForecast", {
                          required: t(
                            "pitch.validation.revenueForecastRequired",
                          ),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.revenueForecast ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t(
                          "pitch.placeholder.enterRevenueForecast",
                        )}
                      />
                      {pitchErrors.revenueForecast && (
                        <div className="invalid-feedback">
                          {pitchErrors.revenueForecast.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.profitMarginsQuestion")}
                      </label>

                      <textarea
                        {...register("profitMargins", {
                          required: t("pitch.validation.profitMarginRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.profitMargins ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterProfitMargins")}
                      />
                      {pitchErrors.profitMargins && (
                        <div className="invalid-feedback">
                          {pitchErrors.profitMargins.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.breakEvenMargins")}
                      </label>

                      <textarea
                        {...register("breakEvenMargins", {
                          required: t("pitch.validation.breakEvenRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.breakEvenMargins ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t(
                          "pitch.placeholder.enterBreakEvenMargins",
                        )}
                      />
                      {pitchErrors.breakEvenMargins && (
                        <div className="invalid-feedback">
                          {pitchErrors.breakEvenMargins.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>{t("pitch.fundingRequirements")}</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        {t("pitch.amountSoughtInGbp")}
                      </label>

                      <input
                        type="number"
                        {...register("amountSought", {
                          required: t("pitch.validation.amountSoughtRequired"),
                          valueAsNumber: true,
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.amountSought ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterAmountSought")}
                      />
                      {pitchErrors.amountSought && (
                        <div className="invalid-feedback">
                          {pitchErrors.amountSought.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">{t("pitch.useOfFunds")}</label>

                      <textarea
                        {...register("useOfFunds", {
                          required: t("pitch.validation.useOfFundsRequired"),
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.useOfFunds ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder={t("pitch.placeholder.enterUseOfFunds")}
                      />
                      {pitchErrors.useOfFunds && (
                        <div className="invalid-feedback">
                          {pitchErrors.useOfFunds.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {formData.status !== "Submitted" ? (
                  <div className="mt-3 d-flex justify-content-end">
                    <Button
                      disabled={isPending}
                      type="submit"
                      variant="primary"
                      size="sm"
                      className="shadow-lg border border-white rounded-3 me-2 d-flex align-items-center justify-content-center px-4 py-2 gap-2"
                    >
                      <BsFloppy />
                      {t("pitch.submitAndContinue")}
                    </Button>
                  </div>
                ) : (
                  <></>
                )}
              </form>
            ) : (
              <Suspense
                fallback={
                  <div className="text-center py-5 text-muted">
                    {t("dashboard.loading")}
                  </div>
                }
              >
                <UploadFiles
                  disableInput={disableInput}
                  formData={formData}
                  setFormData={setFormData}
                  companyProfile={companyProfile}
                />
              </Suspense>
            )}
          </>
        ) : activeTab === "view" ? (
          <Suspense
            fallback={
              <div className="text-center py-5 text-muted">
                {t("dashboard.loading")}
              </div>
            }
          >
            <AllPitches
              pitchesData={pitchesList}
              onEdit={handleEditPitch}
              onDelete={handleDeletePitch}
            />
          </Suspense>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

const CreatePitchMemo = memo(CreatePitch);
CreatePitchMemo.displayName = "CreatePitch";
export default CreatePitchMemo;
